var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useState } from 'react';
import { cx, css as emotionCss } from 'emotion';
import { DebounceInput } from 'react-debounce-input';
import { Input, Styled, styles, validationAccessibility } from '@citrus/input';
import { getAutomation, getDebug, getStyleNames } from '@citrus/runtime';
import { Legacy } from '@citrus/user';
import { enterKeyPressed, keyPressed, removeKeys } from '@pcty/utils';
import ClearButton from './private/ClearButton';
import { SearchIcon } from './Search.styled';
import { inputLarge, inputLargeDisabled, inputSearch, valueLarge } from '../styles/legacy';
var disabledCss = styles.disabled, error = styles.error, focus = styles.focus, input = styles.input, valueCss = styles.value;
var VERSION = '5.2.1';
var Search = function (_a) {
    var _b;
    var _c = _a["aria-label"], ariaLabel = _c === void 0 ? 'Search' : _c, automationId = _a.automationId, id = _a.id, className = _a.className, classNameWrapper = _a.classNameWrapper, 
    // FIXME: check if `css` is needed, or implement it
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    css = _a.css, debounceTimeout = _a.debounceTimeout, disabled = _a.disabled, emptyLabel = _a.emptyLabel, errorMessage = _a.errorMessage, 
    // FIXME: check if `footnote` is needed
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    footnote = _a.footnote, _d = _a.hideOptionalLabel, hideOptionalLabel = _d === void 0 ? false : _d, onBlur = _a.onBlur, onChange = _a.onChange, onClear = _a.onClear, placeholder = _a.placeholder, readOnly = _a.readOnly, _e = _a.showRequiredLabel, showRequiredLabel = _e === void 0 ? false : _e, showValidationState = _a.showValidationState, tooltip = _a.tooltip, valid = _a.valid, value = _a.value, _f = _a.__TYPE, TYPE = _f === void 0 ? 'Search' : _f, other = __rest(_a, ['aria-label', "automationId", "id", "className", "classNameWrapper", "css", "debounceTimeout", "disabled", "emptyLabel", "errorMessage", "footnote", "hideOptionalLabel", "onBlur", "onChange", "onClear", "placeholder", "readOnly", "showRequiredLabel", "showValidationState", "tooltip", "valid", "value", "__TYPE"]);
    var styles = getStyles(className, disabled, false, showValidationState, valid);
    var _g = useState(styles.css), _css = _g[0], setCss = _g[1];
    var _h = useState(styles.styleNames), _styleNames = _h[0], setStyleNames = _h[1];
    useEffect(function () {
        setCss(getStyles(className, disabled, false, showValidationState, valid).css);
    }, [className, disabled, showValidationState, valid]);
    var _onChange = useCallback(function (e) {
        var value = e.target.value;
        if (onChange) {
            onChange({ id: id, value: value, e: e });
        }
    }, [id, onChange]);
    var onKeyPress = useCallback(function (e) {
        if (enterKeyPressed(e)) {
            _onChange(e);
            e.preventDefault();
        }
        if (keyPressed(e, ['Tab'])) {
            _onChange(e);
        }
    }, [_onChange]);
    var onFocus = useCallback(function () {
        var styles = getStyles(className, disabled, true, showValidationState, valid);
        setCss(styles.css);
        setStyleNames(styles.styleNames);
    }, [className, disabled, showValidationState, valid]);
    var _onBlur = useCallback(function () {
        var styles = getStyles(className, disabled, false, showValidationState, valid);
        setCss(styles.css);
        setStyleNames(styles.styleNames);
        if (onBlur) {
            onBlur();
        }
    }, [className, disabled, onBlur, showValidationState, valid]);
    var _onClear = useCallback(function () { return onClear({ id: id }); }, [id, onClear]);
    var emotionObj = (_b = {},
        _b[emotionCss(valueCss)] = true,
        _b[emotionCss(valueLarge)] = true,
        _b);
    var classes = cx(emotionObj);
    var styleNames = {
        value: emotionObj[emotionCss(valueCss)],
        valueLarge: emotionObj[emotionCss(valueLarge)],
    };
    return (React.createElement(Legacy, null,
        React.createElement(Input, { automationId: automationId, className: classNameWrapper, disabled: disabled, emptyLabel: emptyLabel, errorMessage: errorMessage, footnote: other === null || other === void 0 ? void 0 : other.footnote, hideOptionalLabel: hideOptionalLabel, id: id, showRequiredLabel: showRequiredLabel, showValidationState: showValidationState, tooltip: tooltip, valid: valid },
            React.createElement("div", __assign({ className: _css }, getDebug(sessionStorage.getItem('citrus-debug') === 'true', TYPE, VERSION), (sessionStorage.getItem('citrus-debug') === 'true'
                ? { 'data-style': getStyleNames(_styleNames) }
                : {})),
                React.createElement(SearchIcon, { disabled: disabled }),
                React.createElement(Styled.ValueWrapper, null,
                    React.createElement(DebounceInput, __assign({}, removeKeys(other, ['footnote']), getAutomation(automationId, 'input'), validationAccessibility(id, valid, showValidationState), (sessionStorage.getItem('citrus-debug') === 'true'
                        ? { 'data-style': getStyleNames(styleNames) }
                        : {}), { "aria-label": ariaLabel, className: classes, debounceTimeout: debounceTimeout, disabled: disabled, forceNotifyByEnter: false, forceNotifyOnBlur: false, id: id, onBlur: _onBlur, onChange: _onChange, onFocus: onFocus, onKeyDown: onKeyPress, placeholder: placeholder, readOnly: readOnly, type: "text", value: value }))),
                React.createElement(ClearButton, { "aria-label": ariaLabel, automationId: automationId, disabled: disabled, onClick: _onClear, value: value })))));
};
var getStyles = function (className, disabled, hasFocus, showValidationState, valid) {
    var _a;
    var emotionObj = (_a = {},
        _a[emotionCss(input)] = true,
        _a[emotionCss(inputSearch)] = true,
        _a[emotionCss(focus)] = hasFocus,
        _a[emotionCss(inputLarge)] = true,
        _a[emotionCss(error)] = !valid && showValidationState,
        _a[emotionCss(disabledCss)] = disabled,
        _a[emotionCss(inputLargeDisabled)] = disabled,
        _a);
    var css = cx(emotionObj, className);
    var styleNames = {
        input: emotionObj[emotionCss(input)],
        inputSearch: emotionObj[emotionCss(inputSearch)],
        focus: emotionObj[emotionCss(focus)],
        inputLarge: emotionObj[emotionCss(inputLarge)],
        error: emotionObj[emotionCss(error)],
        disabled: emotionObj[emotionCss(disabledCss)],
        inputLargeDisabled: emotionObj[emotionCss(inputLargeDisabled)],
    };
    return { css: css, styleNames: styleNames };
};
Search.defaultProps = {
    'aria-label': 'Search',
    debounceTimeout: 300,
    emptyLabel: false,
    __TYPE: 'Search',
};
export default Search;
